import { Space, SpaceMember } from "@highnote/server/src/core/entities";

/**
 * A stand-in for a space that carries only the fields a list surface renders.
 *
 * The client builds these from a member document's display copy of space
 * fields, so the sidebar, the Library, and the pickers draw their lists with
 * no read of the full space document. A stub carries no role data. A screen
 * that needs roles must fetch the space document, with `useGlobalSpaceCached`,
 * before it acts.
 *
 * The field set is an exact copy of the display fields the server writes onto
 * each member document. If you add a field here and not there, the code
 * compiles and the field is `undefined` at runtime.
 */
export type SpaceStub = Pick<
  Space,
  | "id"
  | "name"
  | "artworkFile"
  | "isArchived"
  | "spaceId"
  | "createdBy"
  | "createdAt"
>;

/**
 * Warning: `member.spaceId` is the space that this membership belongs to, and
 * `member.parentSpaceId` is that space's parent. On a `Space` document,
 * `space.spaceId` means "my parent". The migration plan calls this name
 * collision the single likeliest implementation bug in the migration. That is
 * the reason for the two short comments below.
 *
 * Warning: `member.spaceName` is typed optional, but in practice it must not
 * be absent. The backfill must write it as a non-null string on every
 * `type == 'user'` document. The reason: `orderBy('spaceName')` drops
 * documents that do not have the field, and an explicit `null` sorts before
 * every string. The `|| ""` below renders an empty row instead of a crash;
 * it does not make the document appear in the query. The detector for a
 * missed backfill is the row-count parity test, not this fallback.
 */
export const membershipEntityToSpaceStub = (
  member: SpaceMember,
): SpaceStub => ({
  id: member.spaceId /* the space itself */,
  name: member.spaceName || "",
  artworkFile: member.spaceArtworkFile,
  isArchived: member.isArchived === true,
  spaceId: member.parentSpaceId ?? undefined /* the space's parent */,
  createdBy: member.spaceCreatedBy,
  createdAt: member.spaceCreatedAt,
});
